import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { stopAnalytics } from "@/components/CookieConsent";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Map, Coins, MessageCircleHeart, GraduationCap, FolderClosed, ListChecks,
  Settings, LogOut, Menu, X, Sparkles, UserRound, MailWarning, Store, BookHeart, HeartHandshake,
} from "lucide-react";

const PATH_NAV = [
  { to: "/app/roadmap", label: "Roadmap", icon: Map, testid: "nav-roadmap", count: "roadmap" },
  { to: "/app/funding", label: "Funding Finder", icon: Coins, testid: "nav-funding", count: "funding" },
  { to: "/app/navigator", label: "AI Navigator", icon: MessageCircleHeart, testid: "nav-navigator" },
  { to: "/app/ask", label: "Ask Anything", icon: HeartHandshake, testid: "nav-ask" },
  { to: "/app/roadmap?focus=school_support", label: "School Supports", icon: GraduationCap, testid: "nav-school", match: false },
  { to: "/providers", label: "Find a Provider", icon: Store, testid: "nav-providers", match: false },
];
const ORG_NAV = [
  { to: "/app/documents", label: "Documents", icon: FolderClosed, testid: "nav-documents", count: "documents", paid: true },
  { to: "/app/journal", label: "Journal", icon: BookHeart, testid: "nav-journal", paid: true },
  { to: "/app/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks", count: "tasks", paid: true },
];

const tailValue = (key, counts, isPaid) => {
  if (!counts) return null;
  if (key === "roadmap") return counts.roadmap?.total > 0 ? `${counts.roadmap.done}/${counts.roadmap.total}` : null;
  if (key === "documents" || key === "tasks") return isPaid && counts[key] > 0 ? String(counts[key]) : null;
  return counts[key] > 0 ? String(counts[key]) : null;
};

const linkBase = "flex items-center gap-3 px-3 py-2.5 rounded-[13px] font-poppins font-medium text-[.94rem] transition-colors";
const linkClass = ({ isActive }) =>
  `${linkBase} ${isActive
    ? "bg-kid-tealcta text-white shadow-[0_8px_18px_rgba(21,147,137,.26)] [&_svg]:stroke-white"
    : "text-[#41546A] hover:bg-kid-cream hover:text-kid-navy [&_svg]:stroke-[#7C8CA0] hover:[&_svg]:stroke-kid-teal"}`;

const tailClass = (active) =>
  `ml-auto font-semibold text-[.7rem] px-2 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-kid-skytint text-kid-tealcta"}`;

function VerifyEmailBanner({ user }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      toast.success("Confirmation email sent — check your inbox.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="px-5 sm:px-8 lg:px-10 pt-4" data-testid="verify-email-banner">
      <div className="flex flex-wrap items-center gap-3 bg-kid-sun/15 border border-kid-sun/40 rounded-xl px-4 py-3">
        <MailWarning size={18} className="text-[#B07A11] shrink-0" />
        <p className="text-sm text-kid-navy flex-1 min-w-[200px]">
          Please confirm your email address to secure your account and receive reminders.
        </p>
        <button onClick={resend} disabled={busy || sent} className="kid-btn kid-btn-ghost h-9 px-4 text-sm disabled:opacity-60" data-testid="verify-resend-button">
          {sent ? "Email sent" : busy ? "Sending…" : "Resend confirmation"}
        </button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, isPaid, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    api.get("/dashboard/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, [location.pathname]);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const manageCookies = () => { localStorage.removeItem("kidpath_cookie_consent"); stopAnalytics(); window.location.reload(); };
  const close = () => setOpen(false);

  const { pathname } = location;
  const sectionActive = (to) => pathname === to || pathname.startsWith(to + "/");

  const renderNav = ({ to, label, icon: Icon, testid, count, match }) => {
    const active = match === false ? false : sectionActive(to);
    const tail = count ? tailValue(count, counts, isPaid) : null;
    const aaUnread = testid === "nav-ask" && isPaid ? (counts?.ask_unread || 0) : 0;
    return (
      <Link key={testid} to={to} onClick={close} className={linkClass({ isActive: active })} data-testid={testid} aria-current={active ? "page" : undefined}>
        <Icon size={19} strokeWidth={1.7} /> {label}
        {aaUnread > 0 && (
          <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-kid-coral text-white text-[.7rem] font-bold grid place-items-center leading-none" data-testid="nav-ask-unread">
            {aaUnread}
          </span>
        )}
        {aaUnread === 0 && tail && <span className={tailClass(active)} data-testid={`${testid}-tail`}>{tail}</span>}
      </Link>
    );
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <NavLink to="/app" end onClick={close} className="flex items-center gap-[11px] px-2 pt-1.5 pb-[22px]" data-testid="sidebar-brand">
        <img src="/assets/kidpath-mark-light.png" alt="" className="w-[38px] h-[38px]" />
        <span className="font-display text-2xl font-extrabold leading-none">
          <span style={{ color: "var(--logo-navy)" }}>Kid</span><span style={{ color: "var(--logo-teal)" }}>Path</span>
        </span>
      </NavLink>

      <nav className="flex flex-col gap-[3px] flex-1 overflow-auto">
        <Link to="/app" onClick={close} className={linkClass({ isActive: pathname === "/app" })} data-testid="nav-dashboard" aria-current={pathname === "/app" ? "page" : undefined}>
          <LayoutDashboard size={19} strokeWidth={1.7} /> Dashboard
        </Link>

        <div className="font-poppins font-semibold text-[.66rem] tracking-[.13em] uppercase text-kid-muted/80 px-3 pt-4 pb-[7px]">Your path</div>
        {PATH_NAV.map(renderNav)}

        <div className="font-poppins font-semibold text-[.66rem] tracking-[.13em] uppercase text-kid-muted/80 px-3 pt-4 pb-[7px]">Organize</div>
        {ORG_NAV.map(renderNav)}

        <div className="flex-1" />
        <Link to="/app/account" onClick={close} className={linkClass({ isActive: sectionActive("/app/account") })} data-testid="nav-account" aria-current={sectionActive("/app/account") ? "page" : undefined}>
          <Settings size={19} strokeWidth={1.7} /> Account
        </Link>
      </nav>

      {/* Plan block */}
      <div className="mt-3 border border-kid-line rounded-[20px] p-3.5" style={{ background: "linear-gradient(180deg,#fff,var(--cream))" }}>
        {!isPaid && (
          <NavLink to="/app/account" onClick={close} data-testid="sidebar-upgrade"
            className="flex items-center justify-center gap-2 w-full mb-3 py-[11px] rounded-xl bg-kid-navy text-white font-poppins font-semibold text-[.86rem] transition-transform hover:-translate-y-0.5 [&_svg]:stroke-kid-sun">
            <Sparkles size={15} strokeWidth={2} /> Upgrade
          </NavLink>
        )}
        <div className="flex items-center gap-[11px]">
          <div className="w-[34px] h-[34px] rounded-[11px] bg-kid-skytint grid place-items-center flex-none text-kid-tealcta">
            <UserRound size={18} strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="font-poppins font-semibold text-[.9rem] text-kid-navy leading-tight truncate">{user?.first_name}</div>
            <div className="text-[.76rem] text-kid-muted/80">{isPaid ? "Family Path" : "Calm Start · Free"}</div>
          </div>
          <button onClick={doLogout} data-testid="logout-button" className="ml-auto text-kid-muted hover:text-kid-coral p-1.5" aria-label="Log out"><LogOut size={17} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-kid-mist">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-[264px] bg-white border-r border-kid-line px-4 py-[22px] z-40">
        {SidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur border-b border-kid-line h-16 flex items-center justify-between px-4">
        <NavLink to="/app" end className="flex items-center gap-2">
          <img src="/assets/kidpath-mark-light.png" alt="" className="h-8" />
          <span className="font-display text-xl font-extrabold"><span style={{ color: "var(--logo-navy)" }}>Kid</span><span style={{ color: "var(--logo-teal)" }}>Path</span></span>
        </NavLink>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="w-[42px] h-[42px] rounded-[14px] bg-white border border-kid-line grid place-items-center shadow-sm text-kid-navy"><Menu size={20} /></button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-kid-navy/40" onClick={close} />
          <div className="absolute left-0 inset-y-0 w-[280px] bg-white px-4 py-[22px] shadow-[0_30px_70px_rgba(25,55,80,.14)]">
            <button onClick={close} className="absolute top-5 right-4 p-1 text-kid-muted" data-testid="mobile-menu-close"><X /></button>
            {SidebarInner}
          </div>
        </div>
      )}

      {/* Main — left aligned, full width */}
      <main className="lg:pl-[264px] pt-16 lg:pt-0 min-w-0">
        <VerifyEmailBanner user={user} />
        <div className="px-5 sm:px-8 lg:px-10 pt-6 lg:pt-[30px] pb-8 max-w-[1240px] animate-fade-up">
          <Outlet />
        </div>
        <footer className="px-5 sm:px-8 lg:px-10 pb-10 pt-2 max-w-[1240px]" data-testid="app-footer">
          <div className="border-t border-kid-line pt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-kid-muted">
            <span>© 2026 KidPath · Made in Canada 🍁</span>
            <div className="flex items-center gap-5">
              <Link to="/privacy" className="hover:text-kid-teal" data-testid="app-footer-privacy">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-kid-teal" data-testid="app-footer-terms">Terms</Link>
              <button onClick={manageCookies} className="hover:text-kid-teal" data-testid="app-footer-manage-cookies">Manage cookies</button>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
